import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { StickerButton } from "../Sticker";
import {
  BOARD_ARIA_SECTION,
  BOARD_BLOCKED_COOLDOWN,
  BOARD_BLOCKED_LIMIT,
  BOARD_CHARS_LEFT,
  BOARD_EMPTY,
  BOARD_INTRO,
  BOARD_LABEL_COMPOSER,
  BOARD_LOAD_ERROR,
  BOARD_LOADING,
  BOARD_GUEST_MAX_POSTS,
  BOARD_MAX_CHARS,
  BOARD_POST_BUTTON,
  BOARD_POST_PLACEHOLDER,
  BOARD_POSTING,
  BOARD_RETRY_BUTTON,
} from "../../lib/eventBoard/constants";
import { createBoardMessage, deleteBoardMessage, pinBoardMessage } from "../../lib/eventBoard/api";
import { getGuestPostCooldownMs } from "../../lib/eventBoard/rateLimit";
import EventBoardMessage from "./EventBoardMessage";

export default function EventBoardTab({
  eventId,
  dancerId,
  occurrenceDate,
  board,
  loading,
  error,
  onRetry,
}) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pinningId, setPinningId] = useState(null);
  const [deleteRevealId, setDeleteRevealId] = useState(null);
  const [cooldownMs, setCooldownMs] = useState(0);

  const isHosting = Boolean(board?.is_hosting);
  const myPostCount = board?.my_post_count ?? 0;
  const atPostLimit = !isHosting && myPostCount >= BOARD_GUEST_MAX_POSTS;
  const showCharCount = text.length > BOARD_MAX_CHARS - 50;

  useEffect(() => {
    function tick() {
      setCooldownMs(getGuestPostCooldownMs(board?.messages, dancerId, isHosting));
    }
    tick();
    const iv = window.setInterval(tick, 1000);
    return () => window.clearInterval(iv);
  }, [board?.messages, dancerId, isHosting]);

  useEffect(() => {
    function onPointerDown(e) {
      if (!deleteRevealId) return;
      const openEl = document.querySelector(
        `[data-testid="event-board-message-${deleteRevealId}"]`,
      );
      if (openEl?.contains(e.target)) return;
      setDeleteRevealId(null);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [deleteRevealId]);

  const onCooldown = cooldownMs > 0;
  const canPost = text.trim() && !posting && !atPostLimit && !onCooldown;
  const blockedMessage = atPostLimit
    ? BOARD_BLOCKED_LIMIT
    : onCooldown
      ? BOARD_BLOCKED_COOLDOWN(Math.ceil(cooldownMs / 1000))
      : null;

  async function handlePost(e) {
    e.preventDefault();
    const content = text.trim();
    if (!canPost) return;
    setPosting(true);
    try {
      await createBoardMessage(eventId, { dancerId, occurrenceDate, content });
      setText("");
      await onRetry({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  }

  async function handleDelete(messageId) {
    setDeletingId(messageId);
    try {
      await deleteBoardMessage(eventId, messageId, { dancerId });
      setDeleteRevealId(null);
      await onRetry({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  }

  async function handlePin(messageId, isPinned) {
    setPinningId(messageId);
    try {
      await pinBoardMessage(eventId, messageId, { dancerId, isPinned });
      await onRetry({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPinningId(null);
    }
  }

  if (loading && !board && !error) {
    return (
      <p className="py-6 text-[15px] font-medium text-ink/70" role="status" data-testid="event-board-loading">
        {BOARD_LOADING}
      </p>
    );
  }

  if (error && !board) {
    return (
      <div className="py-8 text-center" data-testid="event-board-error" role="alert">
        <p className="font-ui font-medium text-[15px] text-ink max-w-[36ch] mx-auto">{BOARD_LOAD_ERROR}</p>
        <div className="mt-3">
          <StickerButton onClick={() => onRetry()} data-testid="event-board-retry">
            {BOARD_RETRY_BUTTON}
          </StickerButton>
        </div>
      </div>
    );
  }

  const messages = board?.messages || [];

  return (
    <section className="flex flex-col" data-testid="event-board-tab" aria-label={BOARD_ARIA_SECTION}>
      <p className="mb-4 text-[14px] font-medium text-ink/75 leading-snug max-w-[65ch]">
        {BOARD_INTRO}
      </p>

      {error && board && (
        <p className="mb-3 text-[14px] font-medium text-red-800" role="alert">
          {BOARD_LOAD_ERROR}{" "}
          <button type="button" onClick={() => onRetry()} className="underline font-semibold">
            {BOARD_RETRY_BUTTON}
          </button>
        </p>
      )}

      {messages.length === 0 ? (
        <p className="py-6 text-[15px] font-medium text-ink/70 text-center max-w-[36ch] mx-auto">
          {BOARD_EMPTY}
        </p>
      ) : (
        <div aria-live="polite" aria-relevant="additions">
          {messages.map((message) => (
            <EventBoardMessage
              key={message.id}
              message={message}
              isHosting={isHosting}
              deleteRevealed={deleteRevealId === message.id}
              onDeleteRevealChange={(open) => setDeleteRevealId(open ? message.id : null)}
              onDelete={handleDelete}
              onPin={handlePin}
              deleting={deletingId === message.id}
              pinning={pinningId === message.id}
            />
          ))}
        </div>
      )}

      <form onSubmit={handlePost} className="mt-4 space-y-2">
        <label htmlFor="event-board-composer-text" className="font-ui font-medium text-[14px] text-ink">
          {BOARD_LABEL_COMPOSER}
        </label>
        <textarea
          id="event-board-composer-text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, BOARD_MAX_CHARS))}
          rows={2}
          placeholder={BOARD_POST_PLACEHOLDER}
          maxLength={BOARD_MAX_CHARS}
          disabled={atPostLimit}
          className="field font-ui text-[16px] leading-normal resize-none disabled:opacity-60"
          data-testid="event-board-composer-text"
          aria-describedby="event-board-post-status"
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <StickerButton
            type="submit"
            color="pink"
            disabled={!canPost}
            aria-busy={posting}
            data-testid="event-board-post"
          >
            {posting ? BOARD_POSTING : BOARD_POST_BUTTON}
          </StickerButton>
          <div id="event-board-post-status" className="text-[13px] font-medium text-ink/70 min-w-0">
            {blockedMessage}
            {showCharCount && !blockedMessage && (
              <span aria-live="polite">{BOARD_CHARS_LEFT(BOARD_MAX_CHARS - text.length)}</span>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}
